import { ReactNode, forwardRef, useMemo } from 'react';
import {
  Alert as BaseAlert,
  AlertProps as BaseAlertProps,
  AlertTitle,
  Button,
} from '@mui/material';

import { useAlertStyles } from './useAlertStyles';

export interface IAlertProps extends BaseAlertProps {
  title?: string;
  className?: string;
  type?: 'inline' | 'toast';
  primaryButtonText?: string;
  primaryButtonStartIcon?: ReactNode;
  onPrimaryButtonClick?: () => void;
  secondaryButtonText?: string;
  secondaryButtonStartIcon?: ReactNode;
  onSecondaryButtonClick?: () => void;
}

export const Alert = forwardRef<HTMLDivElement, IAlertProps>(
  (
    {
      title,
      className,
      type = 'toast',
      primaryButtonText,
      primaryButtonStartIcon,
      onPrimaryButtonClick,
      secondaryButtonText,
      secondaryButtonStartIcon,
      onSecondaryButtonClick,
      children,
      ...rest
    }: IAlertProps,
    ref,
  ) => {
    const isInline = type === 'inline';

    const { classes, cx } = useAlertStyles();

    const shouldRenderPrimaryButton =
      primaryButtonText && typeof onPrimaryButtonClick === 'function';
    const shouldRenderSecondaryButton =
      secondaryButtonText && typeof onSecondaryButtonClick === 'function';

    const primaryButton = useMemo(() => {
      if (!shouldRenderPrimaryButton) return null;

      return (
        <Button
          size="small"
          startIcon={primaryButtonStartIcon}
          onClick={onPrimaryButtonClick}
        >
          {primaryButtonText}
        </Button>
      );
    }, [
      onPrimaryButtonClick,
      primaryButtonText,
      primaryButtonStartIcon,
      shouldRenderPrimaryButton,
    ]);

    const secondaryButton = useMemo(() => {
      if (!shouldRenderSecondaryButton) return null;

      return (
        <Button
          variant="text"
          size="small"
          startIcon={secondaryButtonStartIcon}
          className={classes.secondaryButton}
          onClick={onSecondaryButtonClick}
        >
          {secondaryButtonText}
        </Button>
      );
    }, [
      classes.secondaryButton,
      onSecondaryButtonClick,
      secondaryButtonText,
      secondaryButtonStartIcon,
      shouldRenderSecondaryButton,
    ]);

    return (
      <BaseAlert
        ref={ref}
        {...rest}
        className={cx(classes.root, isInline && classes.inlineRoot, className)}
      >
        {title && !isInline && <AlertTitle>{title}</AlertTitle>}
        <div className={classes.childrenWrapper}>
          <div className={cx(isInline && classes.inlineChildren)}>
            {children}
          </div>
          {isInline && primaryButton}
        </div>

        {!isInline && (
          <div className={classes.buttonsWrapper}>
            {primaryButton}
            {secondaryButton}
          </div>
        )}
      </BaseAlert>
    );
  },
);

Alert.displayName = 'Alert';
